/**
 * Utility functions for handling storeId in URLs and localStorage
 */
import { STORE_HOST_SUFFIX } from '../config/runtimeConfig';

// Subdomains that are part of the platform itself and must never be treated as
// a store slug, even if they match the configured host suffix.
export const RESERVED_SUBDOMAINS = new Set([
  'www', 'api', 'app', 'admin', 'mail', 'ftp', 'localhost', 'staging', 'test', 'test-api',
  'cdn', 'static', 'assets', 'auth', 'login', 'register', 'booking', 'book', 'waitlist',
  'support', 'help', 'billing', 'dashboard', 'management', 'store', 'stores',
  'customer', 'customers',
]);

/**
 * Extract the store slug from the current host when running on a store
 * subdomain (e.g. "acme.salovare.com" -> "acme"). Returns null on localhost or
 * when no store-host suffix is configured (path/header mode).
 * @param {string} [host] - Hostname (defaults to window.location.hostname)
 * @returns {string|null} Store slug or null
 */
export const getStoreSlugFromHost = (host) => {
  const suffix = STORE_HOST_SUFFIX;
  if (!suffix) return null;

  const hostname = host || (typeof window !== 'undefined' ? window.location.hostname : '');
  if (!hostname || !hostname.toLowerCase().endsWith(suffix.toLowerCase())) {
    return null;
  }

  const slug = hostname.slice(0, hostname.length - suffix.length).toLowerCase();
  // Reject empty, multi-level (a.b.salovare.com) and reserved subdomains.
  if (!slug || slug.includes('.') || RESERVED_SUBDOMAINS.has(slug)) {
    return null;
  }

  return slug;
};

/**
 * Whether the app is running in subdomain store mode (slug host). In this mode
 * the storeId is resolved from the slug and kept out of the browser URL.
 * @returns {boolean}
 */
export const isSubdomainStoreMode = () => getStoreSlugFromHost() !== null;

/**
 * Validate GUID/UUID format.
 * @param {string} value - Candidate GUID string
 * @returns {boolean} True when value is a valid GUID
 */
export const isValidGuid = (value) => {
  if (!value || typeof value !== 'string') return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
};

/**
 * Get storeId from URL query parameter, localStorage, or return null
 * @param {string} searchParams - URL search params string or object
 * @returns {string|null} storeId or null
 */
export const getStoreIdFromUrl = (searchParams) => {
  if (typeof searchParams === 'string') {
    const params = new URLSearchParams(searchParams);
    return params.get('storeId');
  }
  if (searchParams && typeof searchParams.get === 'function') {
    return searchParams.get('storeId');
  }
  return null;
};

/**
 * Get storeId from URL path (e.g., /stores/123/booking)
 * @param {string} pathname - Current pathname
 * @returns {string|null} storeId or null
 */
export const getStoreIdFromPath = (pathname) => {
  const customerMatch = pathname.match(/^\/stores\/([^/]+)/);
  if (customerMatch && isValidGuid(customerMatch[1])) {
    if (isSubdomainStoreMode()) {
      return null;
    }
    return customerMatch[1];
  }

  const managementMatch = pathname.match(/^\/management\/stores\/([^/]+)/);
  if (managementMatch && isValidGuid(managementMatch[1])) {
    return managementMatch[1];
  }

  return null;
};

/**
 * Get storeId from a request URL path like /stores/{storeId}[/...].
 * Supports relative and absolute URLs.
 * @param {string} requestUrl - Request URL or path
 * @returns {string|null} storeId or null
 */
export const getStoreIdFromRequestUrl = (requestUrl) => {
  if (!requestUrl || typeof requestUrl !== 'string') {
    return null;
  }

  try {
    const resolvedUrl = requestUrl.startsWith('http://') || requestUrl.startsWith('https://')
      ? new URL(requestUrl)
      : new URL(requestUrl, window.location.origin);

    return getStoreIdFromPath(resolvedUrl.pathname);
  } catch (error) {
    return null;
  }
};

/**
 * Get storeId from various sources: path, query params, or localStorage
 * @param {string} pathname - Current pathname
 * @param {string|URLSearchParams} searchParams - URL search params
 * @returns {string|null} storeId or null
 */
export const getStoreId = (pathname, searchParams) => {
  // First try to get from path
  const pathStoreId = getStoreIdFromPath(pathname);
  if (pathStoreId) return pathStoreId;
  
  // Then try query params
  const queryStoreId = getStoreIdFromUrl(searchParams);
  if (queryStoreId) return queryStoreId;
  
  // Finally try localStorage
  return localStorage.getItem('currentStoreId');
};

/**
 * Build a customer route path. In subdomain mode the storeId is implied by the
 * host, so routes are mounted at the root (no /stores/{storeId} prefix). On
 * localhost the storeId stays in the path.
 * @param {string} storeId - Store ID (ignored in subdomain mode)
 * @param {string} route - Route path (e.g., 'booking', 'login'). Empty/'overview' for the overview page
 * @returns {string|null} Customer route path
 */
export const buildCustomerRoute = (storeId, route = '') => {
  const subdomainMode = isSubdomainStoreMode();

  if (!subdomainMode && !storeId) {
    return null;
  }

  const isOverview = !route || route === '' || route === 'overview';
  const suffix = isOverview ? '' : (route.startsWith('/') ? route : `/${route}`);

  if (subdomainMode) {
    return suffix || '/';
  }

  return `/stores/${storeId}${suffix}`;
};

/**
 * Store page a customer should land on after setting a password.
 * Prefers the store already in the URL (slug host or /stores/{id}) so the
 * email link does not depend on localStorage from another origin.
 * @param {{ pathname?: string, queryStoreId?: string|null, storedStoreId?: string|null, subdomainMode?: boolean }} options
 * @returns {string}
 */
export const resolveCustomerStoreHomePath = ({
  pathname = '',
  queryStoreId = null,
  storedStoreId = null,
  subdomainMode = isSubdomainStoreMode(),
} = {}) => {
  if (subdomainMode) {
    return '/';
  }

  const pathStoreId = getStoreIdFromPath(pathname);
  if (pathStoreId) {
    return `/stores/${pathStoreId}`;
  }

  if (isValidGuid(queryStoreId)) {
    return `/stores/${queryStoreId}`;
  }

  if (isValidGuid(storedStoreId)) {
    return `/stores/${storedStoreId}`;
  }

  return '/not-found';
};

/**
 * Build the public booking URL staff can share (e.g. Instagram bio).
 * Deployed (STORE_HOST_SUFFIX set): https://{slug}{suffix}/booking
 * Localhost / path mode: {origin}/stores/{storeId}/booking
 * @param {{ storeId?: string, slug?: string, origin?: string, hostSuffix?: string }} options
 * @returns {string|null}
 */
export const buildPublicStoreBookingUrl = ({
  storeId,
  slug,
  origin = typeof window !== 'undefined' ? window.location.origin : '',
  hostSuffix = STORE_HOST_SUFFIX,
} = {}) => {
  const normalizedSlug = typeof slug === 'string' ? slug.trim().toLowerCase() : '';
  const suffix = hostSuffix || '';

  if (suffix) {
    if (!normalizedSlug) {
      return null;
    }
    return `https://${normalizedSlug}${suffix}/booking`;
  }

  if (!storeId) {
    return null;
  }

  const baseOrigin = origin || 'http://localhost:3000';
  return `${baseOrigin}/stores/${storeId}/booking`;
};

